import { BigNumber } from '@ethersproject/bignumber';
import { ChecksumAddress } from '@silo/types';
import { isUndefined } from '@silo/utils';
import { DEFAULT_SILO_UCRIT } from 'app/constants';
import { calculateUtilization } from 'app/functions/silo/calculations';
import { SiloAsset } from 'app/queries/decoratedSilo';
import { useQueryInterestRateModelConfig } from 'app/queries/siloAssetInterestRateModel';
import { useMemo } from 'react';

export type UtilizationImpact = {
  ucurrent: bigint;
  unew: bigint;
  impact?: Impact;
};

type Status = 'safe' | 'critical';
export interface Impact {
  ucrit: bigint;
  currentStatus: Status;
  newStatus: Status;
}

type UseUtilizationImpact = (params: {
  siloAddress: ChecksumAddress;
  normalizedTokenAmount: BigNumber | null;
  selectedAsset: Pick<SiloAsset, 'liquidity' | 'totalDeposits' | 'assetAddress'>;
  getNewUtilization: GetNewUtilization;
}) => UtilizationImpact;

export const useUtilizationImpact: UseUtilizationImpact = ({
  siloAddress,
  normalizedTokenAmount,
  selectedAsset: { liquidity, totalDeposits, assetAddress },
  getNewUtilization,
}) => {
  const { data, errorUpdateCount } = useQueryInterestRateModelConfig(siloAddress, assetAddress);

  const ucrit = useMemo(() => {
    if (data) return data.ucrit.toBigInt();

    if (errorUpdateCount) return DEFAULT_SILO_UCRIT;

    return undefined;
  }, [data, errorUpdateCount]);

  return useMemo(
    () =>
      getUtilizationImpact({
        ucrit,
        liquidity: liquidity.toBigInt(),
        totalDeposits: totalDeposits.toBigInt(),
        amount: normalizedTokenAmount?.toBigInt() ?? 0n,
        getNewUtilization,
      }),
    [ucrit, liquidity, totalDeposits, normalizedTokenAmount, getNewUtilization]
  );
};

type GetUtilizationImpact = (params: {
  ucrit?: bigint;
  liquidity: bigint;
  totalDeposits: bigint;
  amount: bigint;
  getNewUtilization: GetNewUtilization;
}) => UtilizationImpact;

export type GetNewUtilization = (amount: bigint, totalDeposits: bigint, liquidity: bigint) => bigint;

const getUtilizationImpact: GetUtilizationImpact = ({ ucrit, liquidity, totalDeposits, amount, getNewUtilization }) => {
  const totalBorrows = totalDeposits - liquidity;
  const ucurrent = calculateUtilization(totalDeposits, totalBorrows);
  const unew = getNewUtilization(amount, totalDeposits, liquidity);

  const getImpact = (): UtilizationImpact['impact'] => {
    if (isUndefined(ucrit)) {
      return undefined;
    }

    const currentStatus: Status = ucurrent < ucrit ? 'safe' : 'critical';
    const newStatus: Status = unew < ucrit ? 'safe' : 'critical';

    return {
      ucrit,
      currentStatus,
      newStatus,
    };
  };

  return {
    ucurrent,
    unew,
    impact: getImpact(),
  };
};
